// Browser-global defaults for the REST capture client's injection seams
// (CaptureClientDeps). Production wires these to real window/navigator/document
// globals; tests pass stubs so jsdom quirks don't leak into assertions.
//
// Extracted from capture-client.ts to keep that file under the 500-line gate
// (the #726 logout-flush work pushed it over). Pure, side-effect-free at
// module load — each function only touches globals when called.

export function defaultIsOnline(): boolean {
  if (typeof navigator === 'undefined') return true
  if (typeof navigator.onLine !== 'boolean') return true
  return navigator.onLine
}

export function defaultAddOnlineListener(handler: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener('online', handler)
  return () => window.removeEventListener('online', handler)
}

export function defaultAddOfflineListener(handler: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener('offline', handler)
  return () => window.removeEventListener('offline', handler)
}

export function defaultAddBeforeUnloadListener(
  handler: (event: BeforeUnloadEvent) => void,
): () => void {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener('beforeunload', handler)
  return () => window.removeEventListener('beforeunload', handler)
}

// `pagehide` is the event a mobile OS most reliably delivers before it
// freezes/evicts a backgrounded tab (beforeunload/unload are unreliable on
// iOS, and bfcache restores skip them entirely) — so the PowerSync capture
// client flushes its coalesce on it too (#807). The handler takes no argument;
// the PageTransitionEvent is ignored.
export function defaultAddPageHideListener(handler: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener('pagehide', handler)
  return () => window.removeEventListener('pagehide', handler)
}

export function defaultAddVisibilityChangeListener(
  handler: () => void,
): () => void {
  if (typeof document === 'undefined') return () => {}
  document.addEventListener('visibilitychange', handler)
  return () => document.removeEventListener('visibilitychange', handler)
}

export function defaultGetVisibilityState(): DocumentVisibilityState | 'visible' {
  if (typeof document === 'undefined') return 'visible'
  return document.visibilityState
}
